import {
  CENTRAL_AUTH_PAGE_PATHS,
  CONTROL_TOWER_APP_NAME,
  CONTROL_TOWER_SIGN_IN_CLIENT,
  EXPRESS_SIGN_IN_CLIENT,
  PARTNER_SIGN_IN_CLIENT,
  PRODUCT_NAMES,
  loginRequestSchema,
  type FederatedProviderSlug,
  type LoginRequest,
  type SsoProviderSlug,
} from '@instanexus/contracts';
import { errorMessage, useTranslation } from '@instanexus/i18n';
import { isApiError } from '@instanexus/sdk';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  ProviderButton,
} from '@instanexus/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';

import { AuthCardLayout } from '@/components/auth-card-layout';
import { CompanySignInNotice } from '@/components/company-sign-in-notice';
import { useApiClient } from '@/hooks/use-api-client';
import { forgotPasswordPath } from '@/lib/auth-paths';
import { finishCentralSignIn } from '@/lib/central-session';
import { readFederatedErrorCode } from '@/lib/federated-return';
import { startProviderSignIn } from '@/lib/federated-start';
import { startGoogleSignUp } from '@/lib/google-sign-up';
import { parseSignInRequest, type SignInRequest } from '@/lib/sign-in-request';

/** Not a declared `ErrorCode`; `errorMessage()` falls back to `errors:unexpected`. */
const UNEXPECTED_FAILURE = 'UNEXPECTED';
const SIGN_IN_REQUIRED = 'SIGN_IN_REQUIRED';
const PARTNER_PORTAL_CLOSED = 'PARTNER_PORTAL_CLOSED';

/** Full-page navigation; a function so the unit test can observe it. The only navigation seam. */
function redirectTo(url: string): void {
  window.location.assign(url);
}

const errorCodeOf = (error: unknown): string =>
  isApiError(error) ? error.code : UNEXPECTED_FAILURE;

/** Same normalisation as the platform-admin form: the SAME value is validated and sent. */
const normalizeLoginValues = (values: LoginRequest): LoginRequest => {
  const tenantSlug = values.tenantSlug?.trim() ?? '';
  return {
    email: values.email.trim(),
    password: values.password,
    ...(tenantSlug.length > 0 ? { tenantSlug } : {}),
  };
};

/** The provider a refusal names: `SSO_GOOGLE_REQUIRED` -> 'google', ..., anything else -> null. */
function providerOfSsoCode(code: string | null): SsoProviderSlug | null {
  if (code === 'SSO_GOOGLE_REQUIRED') return 'google';
  if (code === 'SSO_MICROSOFT_REQUIRED') return 'microsoft';
  if (code === 'SSO_ENTERPRISE_REQUIRED') return 'enterprise';
  return null;
}

type Phase = 'checking' | 'reauthenticating' | 'form' | 'error';

/**
 * The central sign-in page (ADR 0038). Reuses a live central session, otherwise shows the
 * password form; either way the browser leaves through the API's `redirectUrl` or the client
 * app's configured origin + a validated relative `next` — never any other user input.
 */
export function SignInPage(): ReactElement {
  const { t } = useTranslation('auth');
  const api = useApiClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const federatedErrorCode = readFederatedErrorCode(location.state);
  const request = useMemo<SignInRequest>(() => parseSignInRequest(searchParams), [searchParams]);

  const isPartner = request.client === PARTNER_SIGN_IN_CLIENT;
  const isExpress = request.client === EXPRESS_SIGN_IN_CLIENT;
  const isControlTower = request.client === CONTROL_TOWER_SIGN_IN_CLIENT;

  const [phase, setPhase] = useState<Phase>('checking');
  const [pageErrorCode, setPageErrorCode] = useState<string | null>(null);
  const [isTenantSlugRequired, setIsTenantSlugRequired] = useState(
    federatedErrorCode === 'FEDERATED_TENANT_REQUIRED',
  );
  const [companyProvider, setCompanyProvider] = useState<SsoProviderSlug | null>(
    providerOfSsoCode(federatedErrorCode),
  );
  const [companyLabel, setCompanyLabel] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(
    providerOfSsoCode(federatedErrorCode) === null ? federatedErrorCode : null,
  );

  const proceed = useCallback(
    (accessToken: string): Promise<void> =>
      finishCentralSignIn(api, request, searchParams.toString(), accessToken, {
        navigate: (to) => void navigate(to),
        redirectTo,
      }),
    [api, navigate, request, searchParams],
  );

  const check = useCallback(async (): Promise<void> => {
    setPhase('checking');
    setPageErrorCode(null);
    try {
      if (isPartner) {
        const session = await api.partnerAuth.refreshSession();
        if (session === undefined) {
          setPhase('form');
          return;
        }
        await proceed(session.accessToken);
        return;
      }
      const outcome = await api.auth.resumeSession();
      if (outcome.kind === 'none') {
        setPhase('form');
        return;
      }
      if (outcome.kind === 'session') {
        await proceed(outcome.response.accessToken);
        return;
      }
      if (outcome.provider === 'enterprise') {
        // No enterprise start flow yet (Phase 6): show the refusal instead of a redirect.
        setErrorCode('SSO_ENTERPRISE_REQUIRED');
        setPhase('form');
        return;
      }
      setPhase('reauthenticating');
      try {
        await startProviderSignIn(
          api,
          outcome.provider,
          { search: searchParams.toString(), email: outcome.email },
          redirectTo,
        );
      } catch (startError: unknown) {
        setErrorCode(errorCodeOf(startError));
        setPhase('form');
      }
    } catch (error: unknown) {
      if (isPartner && isApiError(error) && error.code === PARTNER_PORTAL_CLOSED) {
        setErrorCode(error.code);
        setPhase('form');
        return;
      }
      if (isApiError(error) && error.code === SIGN_IN_REQUIRED) {
        setPhase('form');
        return;
      }
      setPageErrorCode(errorCodeOf(error));
      setPhase('error');
    }
  }, [api, isPartner, proceed, searchParams]);

  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void check();
  }, [check]);

  const validationMessage = errorMessage(t, 'VALIDATION_FAILED');
  const resolver = useMemo<Resolver<LoginRequest>>(() => {
    const validate = zodResolver(loginRequestSchema, {
      path: [],
      async: true,
      errorMap: () => ({ message: validationMessage }),
    });
    return (values, context, options) => validate(normalizeLoginValues(values), context, options);
  }, [validationMessage]);

  const form = useForm<LoginRequest>({
    resolver,
    defaultValues: { email: request.email ?? '', password: '', tenantSlug: '' },
  });

  // Company mode entered from a refusal code: ask discovery once for the IdP's display name.
  const ensureCompanyLabel = async (email: string): Promise<void> => {
    const trimmed = email.trim();
    if (!loginRequestSchema.shape.email.safeParse(trimmed).success) return;
    try {
      const found = await api.sso.discover({ email: trimmed });
      if (found.method === 'company' && found.provider === 'enterprise') {
        setCompanyLabel(found.label ?? null);
      }
    } catch {
      // Advisory: without a label the generic wording is used.
    }
  };

  // Classic-portal customers go back to the classic portal (docs/025). Customer sign-in only:
  // partners, operators and the Express app never had a classic login.
  const routesLegacy = !isPartner && !isControlTower && !isExpress;
  // Leaving the email box sends them before they type a password here. Advisory: submit re-asks,
  // which also covers autofill and Enter, where no blur ever happens.
  const routeLegacyOnBlur = async (email: string): Promise<void> => {
    const trimmed = email.trim();
    if (!routesLegacy || !loginRequestSchema.shape.email.safeParse(trimmed).success) return;
    try {
      const found = await api.sso.discover({ email: trimmed });
      if (found.method === 'legacy') redirectTo(found.url);
    } catch {
      // Advisory: submit asks again.
    }
  };

  const onSubmit = form.handleSubmit(async (values: LoginRequest): Promise<void> => {
    setErrorCode(null);
    if (!isPartner) {
      try {
        const found = await api.sso.discover({ email: values.email.trim() });
        if (found.method === 'legacy' && routesLegacy) {
          redirectTo(found.url);
          return;
        }
        if (found.method === 'company') {
          setCompanyProvider(found.provider);
          setCompanyLabel(found.label ?? null);
          return;
        }
      } catch {
        // Advisory: fall through to the password login, whose 403 is the backstop.
      }
    }
    try {
      const session = isPartner
        ? await api.partnerAuth.login({ email: values.email, password: values.password })
        : await api.auth.login(values);
      await proceed(session.accessToken);
    } catch (error: unknown) {
      const code = errorCodeOf(error);
      const provider = providerOfSsoCode(code);
      if (provider !== null) {
        setCompanyProvider(provider);
        if (provider === 'enterprise') void ensureCompanyLabel(values.email);
        return;
      }
      if (code === 'TENANT_REQUIRED') setIsTenantSlugRequired(true);
      setErrorCode(code);
    }
  });

  // A failed or empty providers answer just means no button; password sign-in is never blocked.
  const providers = useQuery({
    queryKey: ['federation', 'providers'],
    queryFn: () => api.federation.providers(),
    staleTime: Infinity,
    retry: false,
  });
  // Company mode shows its provider whatever the providers answer says: that answer lists social
  // sign-in only (S53-5emp), and company sign-in follows its own flag through discovery and the
  // refusal codes.
  const clientOffersProviders = !isPartner && !isControlTower;
  const offersGoogle =
    clientOffersProviders &&
    (companyProvider === 'google' || (providers.data?.providers.includes('google') ?? false));
  const offersMicrosoft =
    clientOffersProviders &&
    (companyProvider === 'microsoft' || (providers.data?.providers.includes('microsoft') ?? false));
  const offersApple = isExpress && (providers.data?.providers.includes('apple') ?? false);
  const [startingProvider, setStartingProvider] = useState<FederatedProviderSlug | null>(null);
  const [emailFirst, setEmailFirst] = useState(false);

  useEffect(() => {
    const subscription = form.watch((_values, { name }) => {
      if (name === 'email') {
        setEmailFirst(false);
        setCompanyProvider(null);
        setCompanyLabel(null);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  // A callback that returned with a refusal code: fetch the label for the typed email, once.
  const initialEnterprise = providerOfSsoCode(federatedErrorCode) === 'enterprise';
  useEffect(() => {
    if (initialEnterprise) void ensureCompanyLabel(form.getValues('email'));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  const continueWithGoogle = async (): Promise<void> => {
    setErrorCode(null);
    setStartingProvider('google');
    try {
      await startProviderSignIn(
        api,
        'google',
        {
          search: searchParams.toString(),
          email: form.getValues('email'),
          ...(isTenantSlugRequired ? { tenantSlug: form.getValues('tenantSlug') ?? '' } : {}),
        },
        redirectTo,
      );
    } catch (error: unknown) {
      setErrorCode(errorCodeOf(error));
      setStartingProvider(null);
    }
  };

  const continueWithApple = async (): Promise<void> => {
    setErrorCode(null);
    setStartingProvider('apple');
    try {
      await startProviderSignIn(
        api,
        'apple',
        {
          search: searchParams.toString(),
          email: form.getValues('email'),
          ...(isTenantSlugRequired ? { tenantSlug: form.getValues('tenantSlug') ?? '' } : {}),
        },
        redirectTo,
      );
    } catch (error: unknown) {
      setErrorCode(errorCodeOf(error));
      setStartingProvider(null);
    }
  };

  // Microsoft needs the email first: it is the login_hint that routes a work account correctly.
  const continueWithMicrosoft = async (): Promise<void> => {
    setErrorCode(null);
    const email = form.getValues('email').trim();
    if (!loginRequestSchema.shape.email.safeParse(email).success) {
      setEmailFirst(true);
      form.setFocus('email');
      return;
    }
    setEmailFirst(false);
    setStartingProvider('microsoft');
    try {
      const tenantSlug = (form.getValues('tenantSlug') ?? '').trim();
      await startProviderSignIn(
        api,
        'microsoft',
        {
          search: searchParams.toString(),
          email,
          ...(isTenantSlugRequired && tenantSlug.length > 0 ? { tenantSlug } : {}),
        },
        redirectTo,
      );
    } catch (error: unknown) {
      setErrorCode(errorCodeOf(error));
      setStartingProvider(null);
    }
  };

  const continueWithEnterprise = async (): Promise<void> => {
    setErrorCode(null);
    const email = form.getValues('email').trim();
    if (!loginRequestSchema.shape.email.safeParse(email).success) {
      setEmailFirst(true);
      form.setFocus('email');
      return;
    }
    setEmailFirst(false);
    setStartingProvider('enterprise');
    try {
      await startProviderSignIn(
        api,
        'enterprise',
        { search: searchParams.toString(), email },
        redirectTo,
      );
    } catch (error: unknown) {
      setErrorCode(errorCodeOf(error));
      setStartingProvider(null);
    }
  };

  const [isStartingSignUp, setIsStartingSignUp] = useState(false);

  const createAccountWithGoogle = async (): Promise<void> => {
    setIsStartingSignUp(true);
    try {
      await startGoogleSignUp(api, form.getValues('email'), redirectTo);
    } catch (error: unknown) {
      setErrorCode(errorCodeOf(error));
      setIsStartingSignUp(false);
    }
  };
  const offersGoogleSignUp = offersGoogle && !isExpress && errorCode === 'FEDERATED_NO_ACCOUNT';

  if (phase === 'checking' || phase === 'reauthenticating') {
    return (
      <AuthCardLayout>
        <Card className="w-full max-w-md">
          <CardHeader>
            <p role="status" className="text-muted-foreground">
              {phase === 'checking' ? t('central.checking') : t('central.recheck')}
            </p>
          </CardHeader>
        </Card>
      </AuthCardLayout>
    );
  }

  if (phase === 'error') {
    return (
      <AuthCardLayout>
        <Card className="w-full max-w-md">
          <CardHeader>
            <h1 className="text-3xl font-semibold text-primary text-balance">
              {t('central.unavailableTitle')}
            </h1>
            <CardDescription role="alert">
              {errorMessage(t, pageErrorCode ?? UNEXPECTED_FAILURE)}
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button
              type="button"
              onClick={() => {
                void check();
              }}
            >
              {t('central.retry')}
            </Button>
          </CardFooter>
        </Card>
      </AuthCardLayout>
    );
  }

  const isSubmitting = form.formState.isSubmitting;
  const inCompanyMode = companyProvider !== null;

  return (
    <AuthCardLayout>
      <Card className="w-full max-w-md">
        <CardHeader>
          <h1 className="text-3xl font-semibold text-primary text-balance">
            {isPartner
              ? t('platform:partnerPortal.login.title')
              : isControlTower
                ? t('platform:controlTower.login.title', { app: CONTROL_TOWER_APP_NAME })
                : request.client === 'directory'
                  ? t('directory:employeeDirectory.login.title', { app: PRODUCT_NAMES.directory })
                  : t('login.title')}
          </h1>
          <CardDescription>
            {isPartner
              ? t('platform:partnerPortal.login.description')
              : isControlTower
                ? t('platform:controlTower.login.description')
                : request.client === 'directory'
                  ? t('directory:employeeDirectory.login.description')
                  : t('login.description')}
          </CardDescription>
        </CardHeader>

        <Form {...form}>
          <form
            noValidate
            onSubmit={(event) => {
              void onSubmit(event);
            }}
          >
            <CardContent className="flex flex-col gap-4">
              {companyProvider === null ? null : (
                <CompanySignInNotice
                  provider={companyProvider}
                  label={companyLabel}
                  onUseDifferentEmail={() => {
                    setCompanyProvider(null);
                    setCompanyLabel(null);
                    form.setValue('email', '');
                    form.setFocus('email');
                  }}
                />
              )}

              {errorCode === null ? null : (
                <p role="alert" className="text-danger text-sm text-pretty">
                  {errorMessage(t, errorCode)}
                </p>
              )}

              {offersGoogleSignUp ? (
                <div className="flex flex-col gap-2">
                  <p className="text-sm text-muted-foreground">
                    {t('federated.newToInstaCheckin')}
                  </p>
                  <ProviderButton
                    type="button"
                    provider="google"
                    disabled={isStartingSignUp || startingProvider !== null || isSubmitting}
                    aria-busy={isStartingSignUp}
                    onClick={() => {
                      void createAccountWithGoogle();
                    }}
                  >
                    {isStartingSignUp
                      ? t('federated.redirecting')
                      : t('federated.createWithGoogle')}
                  </ProviderButton>
                </div>
              ) : null}

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('login.emailLabel')}</FormLabel>
                    <FormControl>
                      <Input
                        width="full"
                        type="email"
                        inputMode="email"
                        autoComplete="username"
                        autoCapitalize="none"
                        spellCheck={false}
                        {...field}
                        onBlur={() => {
                          field.onBlur();
                          void routeLegacyOnBlur(field.value);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {inCompanyMode ? null : (
                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('login.passwordLabel')}</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          width="full"
                          autoComplete="current-password"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              {isTenantSlugRequired && !inCompanyMode ? (
                <FormField
                  control={form.control}
                  name="tenantSlug"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('login.tenantSlugLabel')}</FormLabel>
                      <FormControl>
                        <Input
                          width="full"
                          autoComplete="organization"
                          autoCapitalize="none"
                          spellCheck={false}
                          {...field}
                          value={field.value ?? ''}
                        />
                      </FormControl>
                      <FormDescription>{t('login.tenantSlugHint')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}
            </CardContent>

            <CardFooter className="flex flex-col items-stretch gap-3">
              {inCompanyMode ? null : (
                <Button
                  type="submit"
                  className="w-full"
                  disabled={isSubmitting}
                  aria-busy={isSubmitting}
                >
                  {isSubmitting ? t('login.submitting') : t('login.submit')}
                </Button>
              )}
              {offersGoogle ||
              offersMicrosoft ||
              offersApple ||
              companyProvider === 'enterprise' ? (
                <>
                  {inCompanyMode ? null : (
                    <p className="text-center text-sm text-muted-foreground">{t('federated.or')}</p>
                  )}
                  {emailFirst ? (
                    <p role="alert" className="text-sm text-destructive">
                      {t('federated.microsoftEmailFirst')}
                    </p>
                  ) : null}
                  {offersGoogle &&
                  companyProvider !== 'microsoft' &&
                  companyProvider !== 'enterprise' ? (
                    <ProviderButton
                      type="button"
                      provider="google"
                      disabled={startingProvider !== null || isSubmitting}
                      aria-busy={startingProvider === 'google'}
                      onClick={() => {
                        void continueWithGoogle();
                      }}
                    >
                      {startingProvider === 'google'
                        ? t('federated.redirecting')
                        : t('federated.continueWithGoogle')}
                    </ProviderButton>
                  ) : null}
                  {offersMicrosoft &&
                  companyProvider !== 'google' &&
                  companyProvider !== 'enterprise' ? (
                    <ProviderButton
                      type="button"
                      provider="microsoft"
                      disabled={startingProvider !== null || isSubmitting}
                      aria-busy={startingProvider === 'microsoft'}
                      onClick={() => {
                        void continueWithMicrosoft();
                      }}
                    >
                      {startingProvider === 'microsoft'
                        ? t('federated.redirectingMicrosoft')
                        : t('federated.continueWithMicrosoft')}
                    </ProviderButton>
                  ) : null}
                  {companyProvider === 'enterprise' ? (
                    <Button
                      type="button"
                      variant="primary"
                      className="w-full"
                      disabled={startingProvider !== null || isSubmitting}
                      aria-busy={startingProvider === 'enterprise'}
                      onClick={() => {
                        void continueWithEnterprise();
                      }}
                    >
                      {startingProvider === 'enterprise'
                        ? t('federated.redirectingEnterprise')
                        : companyLabel === null
                          ? t('federated.continueWithEnterpriseGeneric')
                          : t('federated.continueWithEnterprise', { name: companyLabel })}
                    </Button>
                  ) : null}
                  {offersApple && companyProvider === null ? (
                    <ProviderButton
                      type="button"
                      provider="apple"
                      disabled={startingProvider !== null || isSubmitting}
                      aria-busy={startingProvider === 'apple'}
                      onClick={() => {
                        void continueWithApple();
                      }}
                    >
                      {startingProvider === 'apple'
                        ? t('federated.redirectingApple')
                        : t('federated.continueWithApple')}
                    </ProviderButton>
                  ) : null}
                </>
              ) : null}
              {inCompanyMode ? null : (
                <Button asChild variant="link" className="w-full">
                  <Link to={forgotPasswordPath(request.client)}>{t('login.forgotPassword')}</Link>
                </Button>
              )}
              {isPartner || isExpress ? null : (
                <Button asChild variant="link" className="w-full">
                  <Link to={CENTRAL_AUTH_PAGE_PATHS.signup}>{t('login.createAccount')}</Link>
                </Button>
              )}
            </CardFooter>
          </form>
        </Form>
      </Card>
    </AuthCardLayout>
  );
}
